<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { useDateTime } from '@ai/ui-components'
import type { CustomerIntent } from 'shared/src'
import Trash from '@/assets/img/svg/icons/trash.svg'
import { useToast } from '@ai/ui-components/toast'

const toast = useToast()
const { t } = useI18n()
const { formatDateTime } = useDateTime()

const emit = defineEmits<(e: 'import', intents: CustomerIntent[]) => void>()

const panelOpen = ref(false)
const selectedFile = ref<File | null>(null)
const parsedIntents = ref<CustomerIntent[]>([])
const error = ref(false)
const uploaderRef = ref<{ validate?: () => void } | null>(null)

const MAX_FILE_SIZE = 500 * 1024 // 500kB

const validateIntent = (intent: unknown): intent is CustomerIntent => {
  if (!intent || typeof intent !== 'object') return false

  const obj = intent as Record<string, unknown>

  if (typeof obj.name !== 'string' || !obj.name.trim()) return false
  if (typeof obj.description !== 'string') return false
  if (typeof obj.output !== 'string' || !obj.output.trim()) return false

  if (obj.training_phrases !== undefined) {
    if (!Array.isArray(obj.training_phrases)) return false
    if (!obj.training_phrases.every((phrase) => typeof phrase === 'string')) return false
  }

  return true
}

const openPanel = () => {
  panelOpen.value = true
  selectedFile.value = null
  error.value = false
}

const reset = () => {
  selectedFile.value = null
  parsedIntents.value = []
  error.value = false
}

const closePanel = () => {
  reset()
  panelOpen.value = false
}

watch(selectedFile, (file) => {
  if (!file) return

  if (file.size > MAX_FILE_SIZE) {
    toast.error(t('exceed_the_maximum', ['500kB']), {
      title: t('file_too_big', ['JSON']),
    })
    error.value = true
    return
  }

  const reader = new FileReader()

  reader.onload = (event) => {
    try {
      const json = JSON.parse(event.target?.result as string) as Record<string, unknown>

      if (!json.customer_intents || !Array.isArray(json.customer_intents)) {
        throw new Error('Missing or invalid "customer_intents" array')
      }

      const intents: unknown[] = json.customer_intents
      if (!intents.every((intent: unknown) => validateIntent(intent))) {
        throw new Error('Invalid intent format: missing or invalid required fields')
      }

      parsedIntents.value = intents.filter(validateIntent).map((intent) => ({
        ...intent,
        training_phrases: intent.training_phrases ?? [],
        created_at: new Date().toISOString(),
      }))
      error.value = false
    } catch (e) {
      error.value = true
      console.error(e)
      toast.error(t('invalid_format', ['JSON']), {
        title: t('invalid_file', ['JSON']),
      })

      parsedIntents.value = []
    }
  }

  reader.readAsText(file)
})

const importAndClose = () => {
  uploaderRef.value?.validate?.()
  if (parsedIntents.value.length) {
    emit('import', parsedIntents.value)
    closePanel()
  }
}

defineExpose({
  panelOpen,
  selectedFile,
  parsedIntents,
  error,
  openPanel,
  closePanel,
  importAndClose,
  validateIntent,
})
</script>

<template>
  <ORightPanel v-model:open="panelOpen" :title="t('import_intents')">
    <template #header>
      <OFileUploader
        ref="uploaderRef"
        id="intents-file"
        v-model="selectedFile"
        accept="application/json"
        class="pb-6"
        :strict-file-limit="false"
        :error="error"
      />
    </template>

    <template #default>
      <div v-if="parsedIntents.length" class="space-y-3 text-sm text-gray-700">
        <div
          v-for="(intent, idx) in parsedIntents"
          :key="intent.name"
          class="rounded-lg border border-gray-200 p-4 shadow relative group"
        >
          <div class="flex justify-between items-start">
            <div class="flex flex-col">
              <p class="font-semibold text-base text-gray-900">{{ intent.name }}</p>
              <p class="text-sm text-gray-600 mb-1">{{ intent.description }}</p>
              <p class="text-xs text-gray-500">
                {{ t('output') }}: <span class="text-gray-700">{{ intent.output }}</span>
              </p>
              <p class="text-xs text-gray-400 mt-1">
                {{
                  formatDateTime({ format: 'dateTime', value: intent.created_at, fallback: '-' })
                }}
              </p>
            </div>
            <div class="flex items-center justify-center w-6 h-6">
              <Trash
                class="w-4 h-4 text-gray-500 hover:text-red-500 cursor-pointer"
                @click="parsedIntents = parsedIntents.filter((_, i) => i !== idx)"
              />
            </div>
          </div>
        </div>
      </div>
    </template>

    <template #footer>
      <OButton
        :label="t('cancel')"
        color="neutral"
        variant="secondary"
        size="L"
        @mousedown.prevent
        @click="closePanel"
      />
      <OButton :label="t('save')" size="L" backgroundColor="success" @click="importAndClose" />
    </template>
  </ORightPanel>

  <slot :handle-click="openPanel" />
</template>
